// Bandeau « Switch to English » (Phase 96, plan 96-05 — D-07 / D-07 bis).
//
// Une INVITATION, jamais une instruction. La redirection automatique proposée
// au départ a été écartée : Googlebot explore en anglais américain et serait
// éjecté de la page française, ce que la documentation i18n de Google
// déconseille explicitement — et un expatrié au navigateur anglais qui voulait
// le site français se ferait éjecter tout autant. Ce composant ne doit donc
// JAMAIS toucher à `location` de lui-même ; la suite l'assère au montage.
//
// Coquille reprise trait pour trait de ProfileCompletionBanner : constante de
// clé en tête, état initial synchrone, sortie anticipée, `role="region"`.
// Deux différences, toutes deux nécessaires :
//   · aucune I/O réseau — la condition est purement locale ;
//   · le CTA est un <a href>, pas un lien du routeur : sous `basename="/en"`
//     ce dernier re-préfixerait la cible et produirait /en/en/…
//
// Le rôle ARIA est « region », et surtout aucun des deux rôles d'alerte : c'est
// la convention du dépôt, et plusieurs suites (ProtectedRoute, BlogArticle,
// formulaires) sélectionnent déjà sur le rôle d'alerte — une collision
// détournerait leurs sélecteurs vers ce bandeau.
// Refs: phase 96.
import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useLanguage } from '../i18n/LanguageProvider'
import { langFromPath, withLang } from '../i18n/urlLang'

// Clé DISTINCTE de celle de la langue, et à ne jamais y fusionner : fermer un
// bandeau n'est pas un choix de langue (D-06 de la Phase 95, mot pour mot).
const DISMISS_KEY = 'ss.enBanner.dismissed'

export default function EnglishBanner() {
  const { t, suggested, setLang } = useLanguage()
  const { pathname } = useLocation()
  const [dismissed, setDismissed] = useState(
    () => typeof window !== 'undefined' && localStorage.getItem(DISMISS_KEY) === '1',
  )

  // Les trois termes de D-07 bis, ensemble. C'est `suggested` — la préférence
  // enregistrée, à défaut la détection navigateur — et surtout PAS la langue
  // rendue : sur une URL non préfixée celle-ci vaut toujours « fr » (D-03), un
  // bandeau conditionné à elle ne s'afficherait donc jamais.
  // Le préfixe se lit sur l'URL du DOCUMENT : le chemin du routeur en est déjà
  // dépréfixé par le `basename`, et toute page anglaise passerait pour une
  // page nue.
  const prefixed = typeof window !== 'undefined' && langFromPath(window.location.pathname) !== null
  if (dismissed || suggested !== 'en' || prefixed) return null

  const target = withLang(pathname, 'en')

  function handleDismiss() {
    localStorage.setItem(DISMISS_KEY, '1')
    setDismissed(true)
  }

  // Suivre le bandeau EST un clic explicite : il écrit la préférence (D-05).
  // L'écriture de stockage est synchrone, mais la RPC doit être ATTENDUE — une
  // requête en vol meurt avec le document, et la préférence n'atteindrait
  // jamais le compte (même piège que la bascule de langue, plan 96-03).
  // L'attribut `href` reste la vraie cible : clic milieu, ouverture dans un
  // onglet et lecteurs d'écran ne passent pas par le gestionnaire.
  async function handleFollow(e: React.MouseEvent<HTMLAnchorElement>) {
    e.preventDefault()
    await setLang('en')
    window.location.assign(target)
  }

  return (
    <div
      role="region"
      aria-label={t('enBanner.cta')}
      className="bg-surface border-b border-border px-6 py-3 flex items-center justify-center gap-4 text-sm"
    >
      <p className="text-text-muted m-0">{t('enBanner.text')}</p>
      <a
        href={target}
        onClick={handleFollow}
        className="text-accent hover:text-accent-hover font-medium underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg rounded-[4px]"
      >
        {t('enBanner.cta')}
      </a>
      <button
        type="button"
        aria-label={t('enBanner.dismiss')}
        onClick={handleDismiss}
        className="text-text-muted hover:text-text text-lg leading-none cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg rounded-[4px] px-2"
      >
        ×
      </button>
    </div>
  )
}
